import { useMemo } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import CalendarIcon from '@ancon/wildcat-ui/shared/icons/calendar-event-outline.svg'
import ShoppingBagIcon from '@ancon/wildcat-ui/shared/icons/shopping-bag-outline.svg'
import WalletIcon from '@ancon/wildcat-ui/shared/icons/wallet-outline.svg'
import EditAccountIcon from '@ancon/wildcat-ui/shared/icons/pen-line-outline.svg'
import CustomerIcon from '@ancon/wildcat-ui/shared/icons/customer.svg'
import CompanyIcon from '@ancon/wildcat-ui/shared/icons/company-outline.svg'

import useAppSelector from '../../../../../store/hooks/useAppSelector'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import {
  clientContextCurrentUserCompanyNameSelector,
  clientContextIsAnonymousCustomerSelector,
  clientContextIsCompanyUserSelector,
} from '../../../../clientContext/store/clientContextSelectors'
import {
  authCustomerIsLoggedInSelector,
  authPendingSignOutCheckoutUpdateSelector,
  authSignOutPendingSelector,
} from '../../../../auth/store/authSelectors'
import { setAuthGuestCustomerOnboardingModalVisible } from '../../../../auth/store/authSlice'
import { preOrderUserSelector } from '../../../../preOrder/store/preOrderSelectors'
import useAppSignOut from '../../../../auth/hooks/useAppSignOut'
import { AppRoutes } from '../../../../app/constants'
import isAppWhiteLabeled from '../../../../app/utils/isAppWhiteLabeled'
import { CompanyRoutes } from '../../../../company/constants'
import { ProfileAction } from '../../../constants'
import { ProfileMenuItem, ProfileMenuSection } from '../../../types'

/**
 * The account menu, shared by the navbar menu and the mobile side drawer:
 * company links for company profiles, personal pages, then sign out.
 * Switching profile lives in the "Ordering for" switcher.
 */
export default function useProfileMenuSections(): ProfileMenuSection[] {
  const { t } = useTranslation('common')

  const router = useRouter()
  const dispatch = useAppDispatch()
  const appSignOut = useAppSignOut()

  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)
  const isAnonymousCustomer = useAppSelector(
    clientContextIsAnonymousCustomerSelector,
  )
  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)
  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )
  const preOrderUser = useAppSelector(preOrderUserSelector)
  const isSignOutPending = useAppSelector(authSignOutPendingSelector)
  const isPendingSignOutCheckoutUpdate = useAppSelector(
    authPendingSignOutCheckoutUpdateSelector,
  )

  return useMemo(() => {
    const isCustomer = !!isLoggedIn && !isAnonymousCustomer

    const sections: ProfileMenuSection[] = []

    if (isCompanyUser) {
      sections.push({
        id: 'company',
        label: t('identity.companySection', { companyName }),
        items: [
          {
            id: 'companyOverview',
            label: t('identity.companyOverview'),
            description: t('identity.companyOverviewHint'),
            Icon: CalendarIcon,
            onSelect: () => router.push(CompanyRoutes.Overview),
          },
          {
            id: 'companyDetails',
            label: t('companyDetails.title'),
            description: t('companyDetails.menuHint'),
            Icon: CompanyIcon,
            onSelect: () => router.push(CompanyRoutes.Details),
          },
        ],
      })
    }

    const personalItems: ProfileMenuItem[] = []

    if (isLoggedIn) {
      personalItems.push({
        id: ProfileAction.MyOrders,
        label: t('components.navBar.profile.myOrders'),
        Icon: ShoppingBagIcon,
        onSelect: () => router.push(AppRoutes.Orders),
      })
    }

    if (isCustomer) {
      personalItems.push(
        {
          id: ProfileAction.MyAccount,
          label: t('components.navBar.profile.myAccount'),
          Icon: EditAccountIcon,
          onSelect: () => router.push(AppRoutes.Account),
        },
        {
          id: ProfileAction.MyWallets,
          label: t('components.navBar.profile.myWallets'),
          Icon: WalletIcon,
          onSelect: () =>
            router.push(
              isAppWhiteLabeled()
                ? AppRoutes.WalletsRedirect
                : AppRoutes.Wallets,
            ),
        },
      )
    }

    if (isAnonymousCustomer && (!preOrderUser || !preOrderUser.isHost)) {
      personalItems.push({
        id: ProfileAction.GuestOnBoarding,
        label: isAppWhiteLabeled()
          ? t('components.navBar.profile.createProfile')
          : t('components.navBar.profile.createAnconProfile'),
        Icon: CustomerIcon,
        onSelect: () => {
          dispatch(setAuthGuestCustomerOnboardingModalVisible(true))
        },
      })
    }

    if (personalItems.length) {
      sections.push({
        id: 'personal',
        label: isCompanyUser ? t('identity.personal') : undefined,
        items: personalItems,
      })
    }

    sections.push({
      id: 'session',
      items: [
        {
          id: ProfileAction.SignOut,
          label:
            isAnonymousCustomer || (preOrderUser && !isLoggedIn)
              ? t('components.navBar.profile.exitSession')
              : t('components.navBar.profile.signOut'),
          isDanger: true,
          disabled: isSignOutPending || isPendingSignOutCheckoutUpdate,
          onSelect: appSignOut,
          closesOnSuccess: true,
        },
      ],
    })

    return sections
  }, [
    appSignOut,
    companyName,
    dispatch,
    isAnonymousCustomer,
    isCompanyUser,
    isLoggedIn,
    isPendingSignOutCheckoutUpdate,
    isSignOutPending,
    preOrderUser,
    router,
    t,
  ])
}
